import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  input,
  OnInit,
  Output,
  signal,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";

import { ColDef } from "ag-grid-enterprise";
import { lastValueFrom } from "rxjs";
import { CommonUtils } from "src/app/shared/utils";

import { ConfirmDialogComponent } from "@components/shared";
import { CardContentTypeEnum } from "@components/shared/cards/card-content/card-content.model";
import { AttachmentsCommonService } from "@components/shared/overlay/attachments-common.service";
import { OverlayCommonService } from "@components/shared/overlay/overlay-common.service";
import { SlideOverlayPageService } from "@components/shared/overlay/slide-overlay-page/slide-overlay-page.service";
import { slideOverAnimation } from "@shared/animations";
import { LinkCellRendererComponent } from "@shared/cell-renderers";
import { IconConstants, TextConstants } from "@shared/constants";
import {
  AttachmentTargetEnum,
  AttachmentTypeEnum,
  ConfirmDialogResponseEnum,
  RecordStateEnum,
} from "@shared/enums";
import { IAttachment, IDocument, IDocumentType, IMissingDocumentType } from "@shared/interfaces";
import { NotificationService, AuthenticationService } from "@shared/services";
import { RouterService } from "@shared/services/router.service";

@Component({
  standalone: false,
  selector: "app-overlay-document-attachments",
  templateUrl: "./overlay-document-attachments.component.html",
  styleUrl: "./overlay-document-attachments.component.scss",
  animations: [slideOverAnimation],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class OverlayDocumentAttachmentsComponent implements OnInit, AfterViewInit {
  @ViewChild("attachmentsRightMenu") attachmentsRightMenu: TemplateRef<unknown>;

  @Input() overlayService: OverlayCommonService;

  public isReadOnly = input<boolean>(false);

  attachmentTargetId = input.required<string>();

  attachmentTargetType = input.required<AttachmentTargetEnum>();

  @Output() public selectedAttachmentsUpdated: EventEmitter<void> = new EventEmitter<void>();

  @Output() changes: EventEmitter<void> = new EventEmitter();

  isAvailableAttachmentsLoading = signal<boolean>(true);

  isSelectedAttachmentsLoading = signal<boolean>(true);

  displayConfimDialogUponAttachDocumentsExit = signal<boolean>(false);

  allMissingDocumentTypes = signal<IMissingDocumentType[]>([]);

  allDocumentTypes = signal<IDocumentType[]>([]);

  selectedDocumentsIds = signal<string[]>([]);

  availableDocuments = signal<IDocument[]>([]);

  private selectedAttachments = signal<IAttachment[]>([]);

  public readonly recordStateEnum = RecordStateEnum;

  public readonly hasEntitiesWriteAccess = this.authenticationService.hasEntitiesWriteAccess;

  private readonly shouldGetRulesets =
    this.authenticationService.isRegularUser() &&
    !this.authenticationService.isSystemAdminOrganisation();

  attachTargetUri: string;

  closeOverlay = signal<boolean>(true);

  protected allMissingDocumentTypeColumnDefs = signal<ColDef[]>([]);

  protected documentColumns = signal<string[]>([]);

  protected readonly cardContentTypeEnum = CardContentTypeEnum;

  protected readonly attachmentTypeEnum = AttachmentTypeEnum;

  private searchAvailableAttachmentsText: string = undefined;

  private readonly routerService = inject(RouterService);

  public readonly translations: any = {
    addNewTp: TextConstants.ADD_NEW,
    uploadDocumentsTitle: $localize`:@@shared.attachnewdocuments:Attach new documents`,
    documents: TextConstants.DOCUMENTS,
  };

  constructor(
    private dialog: MatDialog,
    private notification: NotificationService,
    public overlay: SlideOverlayPageService,
    private authenticationService: AuthenticationService,
    private attachmentsCommonService: AttachmentsCommonService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.searchAvailableAttachmentsText = undefined;
    this.setMissingDocumentTypeColumnDefs();
    this.setDocumentColumns();
    await this.loadHelperData();
    await this.loadSelectedAttachments();
  }

  ngAfterViewInit(): void {
    if (this.attachmentsRightMenu) {
      this.overlay.registerLazyLoading({
        panelRef: this.attachmentsRightMenu,
        loadDataFn: async () => {
          await this.loadAvailableAttachments();
        },
        dataSignal: this.availableDocuments,
        shouldReload: false,
      });
    }
  }

  public async loadHelperData(): Promise<void> {
    const allDocumentTypes = await this.overlayService.loadDocumentTypes();

    this.allDocumentTypes.set(allDocumentTypes);
  }

  public async onAddAll(): Promise<void> {
    const availableDocuments = this.availableDocuments();
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: TextConstants.ADD_ALL_CONFIRMATION,
        contentHTML: $localize`:@@confirm.overlay-document-attachments.addalldocuments:Are you sure you want to add all <b>${availableDocuments.length}:count:</b> documents?`,
      },
    });

    dialogRef.afterClosed().subscribe(async (result: ConfirmDialogResponseEnum) => {
      if (result === ConfirmDialogResponseEnum.CONFIRM) {
        this.isSelectedAttachmentsLoading.set(true);
        this.isAvailableAttachmentsLoading.set(true);

        const promises = availableDocuments.map(async (availableDocument) => {
          await this.onAdd(availableDocument, false);
        });

        await Promise.all(promises);

        this.notification.showSuccess(
          $localize`:@@overlay-document-attachments.documentsadded:Documents added`,
        );

        await this.loadSelectedAttachments();
        await this.loadAvailableAttachments();
        this.updateTable();
        this.changes.emit();
      }
    });
  }

  public async onCloseAttachSlideOver(): Promise<boolean> {
    if (!this.displayConfimDialogUponAttachDocumentsExit()) {
      this.displayConfimDialogUponAttachDocumentsExit.set(false);
      this.closeOverlay.set(true);

      return true;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: TextConstants.EXIT_CONFIRMATION,
        contentText: $localize`:@@confirm.discardchanges:You're about to leave this page. Are you sure you want to discard your changes?`,
        confirmButtonText: TextConstants.DISCARD_CHANGES_LEAVE,
        confirmButtonColor: "danger",
        confirmButtonIcon: "logout",
      },
    });

    const response = await lastValueFrom(dialogRef.afterClosed());

    if (response === ConfirmDialogResponseEnum.CONFIRM) {
      this.displayConfimDialogUponAttachDocumentsExit.set(false);
      this.closeOverlay.set(true);

      return true;
    }

    return false;
  }

  public onSearchAvailableAttachments = async (searchText?: string): Promise<void> => {
    this.searchAvailableAttachmentsText = searchText || undefined;

    // Only load if the panel is open
    await this.overlay.loadDataIfPanelOpen(this.attachmentsRightMenu, () =>
      this.loadAvailableAttachments(),
    );
  };

  public async loadAvailableAttachments(): Promise<void> {
    this.isAvailableAttachmentsLoading.set(true);
    const documents = await this.overlayService.loadDocuments(
      this.searchAvailableAttachmentsText || undefined,
    );

    this.availableDocuments.set(
      documents.filter(
        (document) =>
          !this.selectedDocumentsIds().some(
            (selectedDocumentId) => selectedDocumentId === document.id,
          ),
      ),
    );
    this.isAvailableAttachmentsLoading.set(false);
  }

  public async onAdd(attachment: IDocument, isSingleAdd = true): Promise<void> {
    if (isSingleAdd) {
      this.isSelectedAttachmentsLoading.set(true);
      this.isAvailableAttachmentsLoading.set(true);
    }

    try {
      await this.overlayService.createAttachment(
        this.attachmentTargetType(),
        this.attachmentTargetId(),
        this.attachmentTypeEnum.DOCUMENT,
        attachment.id,
      );

      if (isSingleAdd) {
        this.notification.showSuccess(
          $localize`:@@overlay-document-attachments.documentadded:Document added`,
        );
        await this.loadSelectedAttachments();
        await this.loadAvailableAttachments();
        this.updateTable();
        this.changes.emit();
      }
    } catch {
      if (isSingleAdd) {
        this.notification.showError(
          $localize`:@@error.overlay-document-attachments.addingdocument:Error when adding document`,
        );
      }
    }
  }

  public async loadSelectedAttachments(): Promise<void> {
    this.isSelectedAttachmentsLoading.set(true);
    const attachmentTargetId = this.attachmentTargetId();

    const selectedAttachments = (await this.attachmentsCommonService.loadSelectedAttachments(
      this.attachmentTypeEnum.DOCUMENT,
      this.attachmentTargetType(),
      attachmentTargetId,
      true,
    )) as IAttachment[];

    this.selectedAttachments.set(selectedAttachments);

    this.selectedDocumentsIds.set(
      selectedAttachments.map((a) => CommonUtils.getUriId(a.attachmentUri)),
    );

    await this.loadMissingDocumentTypes();
    this.overlayService.documentCounter.set(this.selectedDocumentsIds().length);
    this.selectedAttachmentsUpdated.emit();
    this.isSelectedAttachmentsLoading.set(false);
  }

  public async onRemove(document: IDocument): Promise<void> {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: TextConstants.REMOVE_CONFIRMATION,
        contentText: $localize`:@@confirm.overlay-document-attachments.removedocument:Are you sure you want to remove this document from the record?`,
        confirmButtonColor: "danger",
        confirmButtonText: TextConstants.REMOVE,
        confirmButtonIcon: IconConstants.DELETE,
      },
    });

    dialogRef.afterClosed().subscribe(async (result: ConfirmDialogResponseEnum) => {
      if (result === ConfirmDialogResponseEnum.CONFIRM) {
        try {
          this.isSelectedAttachmentsLoading.set(true);
          this.isAvailableAttachmentsLoading.set(true);
          const selectedAttachment = this.selectedAttachments().find(
            (a) => CommonUtils.getUriId(a.attachmentUri) === document.id,
          );

          await this.overlayService.removeAttachment("document", selectedAttachment.id);
          await this.loadSelectedAttachments();
          await this.loadAvailableAttachments();
          this.updateTable();
          this.changes.emit();
        } catch {
          this.isSelectedAttachmentsLoading.set(false);
          this.isAvailableAttachmentsLoading.set(false);
        }
      }
    });
  }

  public async addNewDocument(): Promise<void> {
    const activeOrganisationId =
      this.overlayService.authenticationService.getActiveOrganisationId();

    this.attachTargetUri = CommonUtils.getTargetUri(
      activeOrganisationId,
      this.attachmentTargetType(),
      this.attachmentTargetId(),
    );
    this.closeOverlay.set(false);
  }

  public onAttachDocumentsCompleted = async (result: {
    attachedIds: string[];
    uploadedIds: string[];
  }): Promise<void> => {
    this.closeOverlay.set(true);

    if (result?.attachedIds?.length || result?.uploadedIds?.length) {
      await this.loadHelperData();
      if (result?.attachedIds?.length) {
        this.notification.showSuccess(
          result.attachedIds.length > 1
            ? $localize`:@@overlay-document-attachments.documentsadded:Documents added`
            : $localize`:@@overlay-document-attachments.documentadded:Document added`,
        );
        await this.loadSelectedAttachments();
      }
      if (result?.uploadedIds?.length) {
        await this.loadAvailableAttachments();
      }
    }
  };

  public onFilesAdded(): void {
    this.displayConfimDialogUponAttachDocumentsExit.set(true);
  }

  private async loadMissingDocumentTypes(): Promise<void> {
    if (this.attachmentTargetType() === AttachmentTargetEnum.PURCHASE_ORDERS) {
      return;
    }
    this.allMissingDocumentTypes.set([]);
    this.overlayService.hasMissingDocumentTypes.set(false);

    if (this.shouldGetRulesets) {
      const allMissingDocumentTypes = await this.overlayService.allMissingDocumentTypesByRuleSet(
        this.attachmentTargetType(),
        this.attachmentTargetId(),
        this.allDocumentTypes(),
      );

      this.allMissingDocumentTypes.set(allMissingDocumentTypes);
      this.overlayService.hasMissingDocumentTypes.set(!!allMissingDocumentTypes.length);
    }
  }

  private setDocumentColumns = (): void => {
    const documentColumns = ["recordState", "name", "type.name"];

    if (this.shouldGetRulesets) {
      documentColumns.push("rulesetsNames");
    }

    documentColumns.push(...["issuance", "validityStart", "validityEnd", "tags"]);

    this.documentColumns.set(documentColumns);
  };

  private setMissingDocumentTypeColumnDefs(): void {
    this.allMissingDocumentTypeColumnDefs.set([
      {
        headerName: TextConstants.TYPE,
        field: "types",
        lockVisible: true,
      },
      {
        headerName: TextConstants.RULESET,
        field: "ruleset.name",
        cellRenderer: LinkCellRendererComponent,
        cellRendererParams: {
          linkRouteFn: this.routerService.getRulesetLink,
          linkRouteIdParam: "ruleset.id",
          openInNewTab: true,
        },
      },
    ]);
  }

  private updateTable(): void {
    // We need to update the tables so the rulesets are re-calculated.
    if (
      this.attachmentTargetType() === AttachmentTargetEnum.DELIVERY ||
      this.attachmentTargetType() === AttachmentTargetEnum.LOCATION
    ) {
      this.overlay.updateTable();
    }
  }
}
